import { useEffect, useState } from 'react'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useProcedureProducts } from '@/hooks/useProducts'
import { actionableProducts } from '@/lib/productSupersession'
import { LisReleasePanel } from '@/components/procedure/LisReleasePanel'
import { defaultProductId } from '@/components/procedure/LisOrderDialog'

/**
 * Send to Epic from the procedure page (z8nrz7ffzv / z8nrz7fgcp): the same
 * panel as on the product page, for one of this procedure's products. Opened
 * from the form's action menu next to Order Test Results, or from a row of the
 * products list with that product already chosen.
 *
 * The values come from the forms, not the product: the product is how a send
 * is tied to its procedure and patient. Nothing is ordered first (MedStar:
 * results and PDFs both go unsolicited).
 */
export function LisSendToEpicDialog({
  open,
  onOpenChange,
  procedureId,
  initialProductId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  procedureId: string | undefined
  /** The row's product when opened from the products list; otherwise the newest. */
  initialProductId?: string | null
}) {
  const products = useProcedureProducts(procedureId ?? null, open)
  // Superseded products are history, not bags (z8nrz7e72t).
  const options = actionableProducts(products.data ?? [])
  const [productId, setProductId] = useState('')

  useEffect(() => {
    if (open) setProductId(initialProductId ?? '')
  }, [open, initialProductId])

  // Fill an empty choice once the list arrives, as Order Test Results does.
  useEffect(() => {
    if (!open || productId !== '' || options.length === 0) return
    setProductId(defaultProductId(options))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, products.data])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] max-w-4xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Send to Epic</DialogTitle>
          <DialogDescription>
            Release a product&apos;s test results to Epic, or send its PDF report, once the form is completed. Neither
            needs an order: Title21 makes the order number when it sends.
          </DialogDescription>
        </DialogHeader>

        <div className='grid max-w-md gap-1.5'>
          <Label htmlFor='lis-epic-product'>Product</Label>
          {products.isPending ? (
            <p className='text-sm text-muted-foreground'>Loading products…</p>
          ) : products.isError ? (
            <p className='text-sm text-destructive'>Could not load this procedure&rsquo;s products.</p>
          ) : options.length === 0 ? (
            <p className='text-sm text-muted-foreground'>This procedure has no products yet.</p>
          ) : (
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger id='lis-epic-product'>
                <SelectValue placeholder='Choose a product…' />
              </SelectTrigger>
              <SelectContent>
                {options.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.din}
                    {p.productDescriptionCode ? ` · ${p.productDescriptionCode}` : ''}
                    {p.volumeMl != null ? ` · ${p.volumeMl} mL` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {productId ? <LisReleasePanel key={productId} productId={productId} /> : null}
      </DialogContent>
    </Dialog>
  )
}
